import { useEffect, useMemo } from 'react'
import useProgressBar from 'features/ad/progressBar/useProgressBar'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import { createKeywords, reset } from 'features/ad/keyword/keywordSlice'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import Button from 'components/form/Button'
import { useConfirm } from 'features/dialog/Confirm'
import { isEmptyObject } from 'utils'
import useDashboardReferrer, {
  DASHBOARD_REFERRER,
} from 'features/ad/useDashboardReferrer'
import PropTypes from 'prop-types'
import useKeywordGenerable from 'features/ad/keyword/hooks/useKeywordGenerable'
import {
  BiddingStrategyTypes,
  CampaignTypes,
  DashboardTypes,
  MaxBiddingAmountChangeTypes,
} from 'features/Codes'
import api from 'api/axios/foka'
import useKeywordValidator from './hooks/useKeywordValidator'
import AddKeywords from './form/AddKeywords'
import RecommendKeywords from './form/RecommendKeywords'
import BiddingStrategy from './form/BiddingStrategy'
import CombinationKeywords from './form/CombinationKeywords'
import { reset as adGroupReset } from '../group/adGroupSlice'
import { reset as campaignReset } from '../campaign/campaignSlice'

const progress = {
  [DASHBOARD_REFERRER.CAMPAIGN]: [
    {
      text: '캠페인 만들기',
      done: true,
    },
    {
      text: '광고그룹 만들기',
      done: true,
    },
    {
      text: '키워드 만들기',
      active: true,
    },
    {
      text: '소재 선택하기',
    },
  ],
  [DASHBOARD_REFERRER.AD_GROUP]: [
    {
      text: '광고그룹 만들기',
      done: true,
    },
    {
      text: '키워드 만들기',
      active: true,
    },
    {
      text: '소재 선택하기',
    },
  ],
  [DASHBOARD_REFERRER.KEYWORD]: null,
}

const Keyword = ({ modify = false }) => {
  const referrer = useDashboardReferrer()
  useProgressBar(
    modify ? null : progress[referrer || DASHBOARD_REFERRER.CAMPAIGN]
  )
  useKeywordGenerable()
  const dispatch = useDispatch()
  const history = useHistory()
  const { campaignId, adGroupId } = useParams()
  const { accountId: adAccountId } = useParams()
  const {
    campaign: {
      form: { campaignType },
      isLoading: isCampaignLoading,
    },
    adGroup: { isLoading: isAdGroupLoading },
    keyword: {
      isSubmitting,
      form: {
        texts,
        suggestions,
        biddingStrategyType,
        maxBiddingAmountChangeType,
      },
      biddingAverages: {
        content: biddingAverageContent,
        loaded: biddingAverageLoaded,
      },
    },
  } = useSelector(state => state.ad)
  const isLoading = useMemo(
    () => isSubmitting || isCampaignLoading || isAdGroupLoading,
    [isSubmitting, isCampaignLoading, isAdGroupLoading]
  )
  const { errors, validate } = useKeywordValidator()
  const [PrevConfirm, showPrevConfirm] = useConfirm()

  useEffect(
    () => () => {
      batch(() => {
        dispatch(reset())
        dispatch(adGroupReset())
        dispatch(campaignReset())
      })
    },
    [dispatch]
  )
  const handleSubmit = async () => {
    try {
      await validate()
      const keywords = [...texts, ...suggestions]
      if (isEmptyObject(errors)) {
        let biddingAverages = biddingAverageContent
        if (
          biddingStrategyType !== BiddingStrategyTypes.INHERIT_FROM_ADGROUP &&
          maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK &&
          !biddingAverageLoaded
        ) {
          const {
            data: { results },
          } = await api.recommendKeyword.getBiddingAverages(keywords)
          biddingAverages = results
        }
        const { error, payload } = await dispatch(
          createKeywords(biddingAverages)
        )
        if (error) {
          const { errorCode, errorMessage } = payload
          if (errorCode === 105006) {
            fail(errorMessage)
          } else if (errorCode === 105004) {
            fail(
              '등록 가능한 키워드 개수를 초과하였습니다.\n미사용 키워드를 삭제 후 다시 시도하세요.'
            )
          } else if (errorCode === 105002) {
            fail(
              `입력 불가능한 문자가 입력되어 있습니다.\n(한글, 영문, 숫자, 특수문자 일부, 띄어쓰기 외 입력 불가)`
            )
          } else if (errorCode === 105001 || errorCode === 105014) {
            fail(
              `키워드는 ${
                campaignType === CampaignTypes.TALK_CHANNEL ? 2 : 1
              }자~50자 범위 내에서 입력하세요.`
            )
          } else {
            failWithErrorResponse(payload, {
              defaultMessage:
                '알 수 없는 이유로 키워드를 저장할 수 없습니다. \n다시 시도하세요.',
            })
          }
        } else {
          const { oldKeywordSize, newKeywordSize } = payload
          if (oldKeywordSize > 0) {
            success(
              `이미 등록된 키워드 ${oldKeywordSize}개를 제외하고\n키워드가 ${newKeywordSize}개 저장되었습니다.`
            )
          } else {
            success(`키워드가 ${newKeywordSize}개 저장되었습니다.`)
          }
          if (modify) {
            history.push(
              `/${adAccountId}/afterCreate?type=${DashboardTypes.KEYWORD}&campaignId=${campaignId}&adGroupId=${adGroupId}`
            )
          } else {
            history.push(
              `/${adAccountId}/ads/campaigns/${campaignId}/groups/${adGroupId}/creatives${
                referrer ? `?from=${referrer}` : ''
              }`
            )
          }
        }
      }
    } catch (error) {
      if (error.texts) {
        fail(error.texts)
      }
    }
  }
  return (
    <>
      <LoadingWrapper isLoading={isLoading}>
        <div className="wrap_adcreate">
          <div className="ad_managebox">
            <div className="managebox_tit">
              <strong className="tit_box">키워드</strong>
            </div>
            <div className="tfkeyword_wrap">
              <AddKeywords />
              {campaignType === CampaignTypes.TALK_CHANNEL ? (
                <CombinationKeywords />
              ) : (
                <RecommendKeywords />
              )}
            </div>
          </div>
          {campaignType === CampaignTypes.PREMIUM_LINK && (
            <BiddingStrategy errors={errors} validate={validate} />
          )}
          <div className="page_btn">
            <div className="inner_btn">
              <Button className="btn_gb" onClick={showPrevConfirm}>
                취소
              </Button>
              <Button
                className="btn_gb gb_bl"
                onClick={handleSubmit}
                loading={isSubmitting}>
                {modify ? '저장' : '저장하고 계속'}
              </Button>
            </div>
          </div>
        </div>
      </LoadingWrapper>
      <PrevConfirm
        title="키워드 만들기 취소"
        onClose={({ isConfirmed }) => {
          if (isConfirmed) {
            if (modify) {
              history.push(`/${adAccountId}/dashboard`)
            } else {
              history.push(
                `/${adAccountId}/afterCreate?type=${DashboardTypes.AD_GROUP}&campaignId=${campaignId}&adGroupId=${adGroupId}`
              )
            }
          }
        }}>
        키워드 만들기를 취소하시겠습니까?
        <br />
        저장되지 않은 모든 내용은 사라집니다.
      </PrevConfirm>
    </>
  )
}

Keyword.propTypes = {
  modify: PropTypes.bool,
}

export default Keyword
